import { CommuteMode } from "../../util/tflFareHeuristic";
import { SupermarketPoi } from "../../util/mapboxSupermarkets";
import { RouteResult } from "../../util/mapboxDirections";
import { isValidUkPostcode } from "../../util/ukPostcode";

export type DayType = "work" | "student" | "parent";

export interface ExtraStop {
  label: string;
  postcode: string;
  longitude?: number | null;
  latitude?: number | null;
}

export interface DaySchedule {
  arriveByTime: string;
  leaveAtTime: string;
  schoolDropoffTime: string;
  schoolPickupTime: string;
}

export interface ResolvedStop {
  id: string;
  label: string;
  name: string;
  address?: string;
  longitude: number;
  latitude: number;
  type: "home" | "work" | "school" | "shop" | "extra";
}

export interface MyDayLeg {
  from: ResolvedStop;
  to: ResolvedStop;
  route: RouteResult | null;
  phase: "morning" | "evening";
}

export interface MyDayPlan {
  dayType: DayType;
  schedule: DaySchedule;
  stops: ResolvedStop[];
  legs: MyDayLeg[];
  supermarkets: SupermarketPoi[];
  primarySupermarket: SupermarketPoi | null;
  totalDurationSeconds: number;
  totalDistanceMeters: number;
}

export interface CostOfLivingFormState {
  dayType: DayType;
  primaryPostcode: string;
  workPostcode: string;
  arriveByTime: string;
  leaveAtTime: string;
  schoolDropoffTime: string;
  schoolPickupTime: string;
  daysPerWeek: number;
  commuteMode: CommuteMode;
  extraStops: ExtraStop[];
  selectedSupermarketIds: string[];
  shopsPerWeek: number;
  occupants: number;
  householdIncome: number;
  incomeIsAnnual: boolean;
  includeBills: boolean;
  includeTvLicence: boolean;
  includeContentsInsurance: boolean;
  /** @deprecated legacy session fields */
  workplaceQuery?: string;
  workplaceMapboxId?: string;
}

export const DEFAULT_FORM_STATE: CostOfLivingFormState = {
  dayType: "work",
  primaryPostcode: "",
  workPostcode: "",
  arriveByTime: "09:00",
  leaveAtTime: "17:30",
  schoolDropoffTime: "08:30",
  schoolPickupTime: "15:30",
  daysPerWeek: 3,
  commuteMode: "tube",
  extraStops: [{ label: "", postcode: "" }],
  selectedSupermarketIds: ["tesco"],
  shopsPerWeek: 1,
  occupants: 1,
  householdIncome: 0,
  incomeIsAnnual: true,
  includeBills: true,
  includeTvLicence: false,
  includeContentsInsurance: false,
};

export const GLOBAL_LIVING_KEY = "le-living-preferences";

export function sessionKey(propertyId: number): string {
  return `le-mday-${propertyId}`;
}

export type LivingToolsTab = "affordability" | "plan" | "cost";

export interface PropertyLivingProperty {
  id: number;
  price: number;
  rent_frequency: string;
  deposit: number | null;
  postcode: string;
  latitude: number;
  longitude: number;
  bedrooms: number | null;
  internal_area: number | null;
  description: string[];
}

/** Migrate legacy sessionStorage shape to new postcode-based fields. */
export function migrateFormState(
  raw: Partial<CostOfLivingFormState>
): CostOfLivingFormState {
  const merged = { ...DEFAULT_FORM_STATE, ...raw };

  if (!merged.primaryPostcode && merged.workplaceQuery) {
    merged.primaryPostcode = merged.workplaceQuery;
  }

  if (!merged.extraStops?.length) {
    merged.extraStops = [{ label: "", postcode: "" }];
  }

  if (merged.dayType === "parent" && !merged.workPostcode && merged.workplaceQuery) {
    merged.workPostcode = "";
  }

  return merged;
}

export function canPlanMyDay(form: CostOfLivingFormState): boolean {
  if (!isValidUkPostcode(form.primaryPostcode)) return false;

  if (form.dayType === "parent" && form.workPostcode.trim()) {
    if (!isValidUkPostcode(form.workPostcode)) return false;
  }

  for (const stop of form.extraStops) {
    const hasLabel = stop.label.trim().length > 0;
    const hasPostcode = stop.postcode.trim().length > 0;
    if (hasLabel && !isValidUkPostcode(stop.postcode)) return false;
    if (!hasLabel && hasPostcode && !isValidUkPostcode(stop.postcode)) return false;
  }

  return true;
}
